const toAmount = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const roundPercent = (amount, collateralValue) => (
  collateralValue > 0
    ? Number(((amount / collateralValue) * 100).toFixed(2))
    : 0
);

// Refinance pricing uses the current appraised value when an appraisal is
// supplied. If the form has no active appraisal override, Home value is the
// provisional collateral value used for the search.
export const getEffectiveAppraisedValue = ({
  propertyValue,
  appraisedValue,
  useAppraisal = true,
}) => {
  const property = toAmount(propertyValue);
  if (!useAppraisal || appraisedValue === undefined || appraisedValue === null || appraisedValue === "") {
    return property;
  }
  return toAmount(appraisedValue);
};

export const getCollateralValue = ({ loanPurpose, propertyValue, appraisedValue }) => {
  const property = toAmount(propertyValue);
  const appraisal = getEffectiveAppraisedValue({ propertyValue, appraisedValue });

  return String(loanPurpose || "").toLowerCase() === "purchase"
    ? Math.min(property, appraisal)
    : appraisal;
};

// Canonical first-lien amounts used by both the form and the request payload.
// For Purchase, the first loan is price minus down payment. For refinance, the
// Loan amount field is already the new note amount. Total loan includes any
// subordinate financing unless the user explicitly supplies an override.
export const calculateFirstLienAmounts = ({
  loanPurpose,
  propertyValue,
  downPayment,
  loanAmount,
  secondaryLoanAmount,
  totalLoanAmount,
}) => {
  const baseLoanAmount = String(loanPurpose || "").toLowerCase() === "purchase"
    ? Math.max(0, toAmount(propertyValue) - toAmount(downPayment))
    : toAmount(loanAmount);
  const hasTotalOverride = totalLoanAmount !== undefined
    && totalLoanAmount !== null
    && totalLoanAmount !== "";

  return {
    baseLoanAmount,
    totalLoanAmount: hasTotalOverride
      ? toAmount(totalLoanAmount)
      : baseLoanAmount + toAmount(secondaryLoanAmount),
  };
};

export const calculateLoanMetrics = ({
  loanProgram,
  loanPurpose,
  propertyValue,
  appraisedValue,
  loanAmount,
  secondaryLoanAmount,
  primaryMortgageAmount,
  lineAmount,
  drawAmount,
}) => {
  const collateralValue = getCollateralValue({
    loanPurpose,
    propertyValue,
    appraisedValue,
  });
  const subjectAmount = loanProgram === "heloc"
    ? toAmount(drawAmount)
    : toAmount(loanAmount);
  const ltv = roundPercent(subjectAmount, collateralValue);

  if (loanProgram === "heloc") {
    return {
      collateralValue,
      ltv,
      cltv: roundPercent(
        toAmount(primaryMortgageAmount) + toAmount(drawAmount),
        collateralValue,
      ),
      hcltv: roundPercent(
        toAmount(primaryMortgageAmount) + toAmount(lineAmount),
        collateralValue,
      ),
    };
  }

  if (loanProgram === "secondMortgage") {
    const combined = roundPercent(
      toAmount(primaryMortgageAmount) + subjectAmount,
      collateralValue,
    );
    return { collateralValue, ltv, cltv: combined, hcltv: combined };
  }

  const combined = roundPercent(
    subjectAmount + toAmount(secondaryLoanAmount),
    collateralValue,
  );
  return { collateralValue, ltv, cltv: combined, hcltv: combined };
};

// CLTV/HCLTV are editable only in the first-lien Advanced block.  Keep the
// selection rule here so the displayed values and the request body cannot
// diverge. Second-lien ratios always come from the line/balance structure.
export const resolveLoanMetricOverrides = ({
  lienPosition,
  derivedMetrics,
  cltvOverride,
  hcltvOverride,
}) => ({
  ...derivedMetrics,
  cltv: lienPosition === "first" && cltvOverride !== undefined && cltvOverride !== null
    ? toAmount(cltvOverride)
    : derivedMetrics.cltv,
  hcltv: lienPosition === "first" && hcltvOverride !== undefined && hcltvOverride !== null
    ? toAmount(hcltvOverride)
    : derivedMetrics.hcltv,
});
